/**
 * The offline capture outbox, CLAUDE.md hard rule 4.
 *
 * A capture that cannot reach the server (POST /api/badge/scan never runs
 * offline at all, since it also does the Anthropic OCR call; a photo upload
 * or POST /api/contacts fails with the network-unreachable signal
 * base44Client.js's request() already produces, ApiError.status === 0) is
 * written here instead of being lost. One IndexedDB object store, one record
 * per queued contact, keyed by the same client_uuid the server already
 * deduplicates on (worker/routes/contacts.ts), so a retry after a kill can
 * never create a duplicate contact.
 *
 * Uses idb, a small promise wrapper around the native IndexedDB API, added
 * 2026-09-08 (CLAUDE.md hard rule 6): the raw callback-based API is exactly
 * the kind of code that has to be reliable, not dependency-free.
 *
 * Photos are stored here as Blobs, not yet uploaded: a badge photo captured
 * offline has never been through POST /api/badge/scan or POST /api/images,
 * so there is no R2 key for it yet. flushOne below uploads them (through the
 * plain, OCR-free POST /api/images, never /api/badge/scan: the rep already
 * typed the fields by hand, so there is nothing left for OCR to do) the
 * moment a sync attempt actually reaches the server.
 */

import { openDB } from 'idb'
import { base44 } from '@/api/base44Client'

const DB_NAME = 'tradesync-outbox'
const DB_VERSION = 1
const STORE = 'pending_contacts'

/**
 * How many times a record may be rejected by the server itself before it is
 * shown as stuck rather than merely waiting.
 *
 * Deliberately not counting a network-unreachable attempt (status 0) toward
 * this cap: being offline for a long time at a show is the expected, normal
 * case this whole module exists for, not evidence anything is wrong. Only a
 * real response from the server (a 400 for a bad payload, a stubborn 500)
 * means a retry will keep failing the same way, since nothing about the
 * stored payload changes between attempts.
 */
export const MAX_SERVER_ATTEMPTS = 3

let dbPromise = null
function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        db.createObjectStore(STORE, { keyPath: 'clientUuid' })
      },
    })
  }
  return dbPromise
}

// ---------------------------------------------------------------------------
// subscription, so the Home banner re-renders without polling
// ---------------------------------------------------------------------------

const listeners = new Set()

function notifyChanged() {
  for (const fn of listeners) fn()
}

/** Fires after any queue mutation or flush attempt. Returns an unsubscribe. */
export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// ---------------------------------------------------------------------------
// queue mutation
// ---------------------------------------------------------------------------

/**
 * Queue a capture that could not reach the server.
 *
 * Upserts by clientUuid: a rep retrying Save on the same still-open form
 * reuses the same clientUuid NewMeeting.jsx already mints once per mount, so
 * this replaces the earlier attempt rather than adding a second queued
 * contact for the same capture.
 */
export async function queueCapture({
  clientUuid,
  payload,
  badgeBlob = null,
  badgeBackBlob = null,
  personBlob = null,
}) {
  const db = await getDb()
  await db.put(STORE, {
    clientUuid,
    payload,
    badgeBlob,
    badgeBackBlob,
    personBlob,
    badgeImageKey: null,
    badgeBackImageKey: null,
    personImageKey: null,
    createdAt: Date.now(),
    serverAttemptCount: 0,
    lastAttemptAt: null,
    lastError: null,
    stuck: false,
    authExpired: false,
  })
  notifyChanged()
}

async function updateRecord(clientUuid, patch) {
  const db = await getDb()
  const existing = await db.get(STORE, clientUuid)
  if (!existing) return
  await db.put(STORE, { ...existing, ...patch })
}

async function deleteRecord(clientUuid) {
  const db = await getDb()
  await db.delete(STORE, clientUuid)
}

/**
 * A rep giving up on a record from the Stuck Captures screen (task: stuck
 * captures, 2026-09-08). Same operation as the internal deleteRecord above,
 * exported under its own name since "discard" is a deliberate, user facing
 * decision, not an implementation detail.
 */
export async function discardRecord(clientUuid) {
  await deleteRecord(clientUuid)
  notifyChanged()
}

// ---------------------------------------------------------------------------
// reading the queue
// ---------------------------------------------------------------------------

/** Every queued record, oldest first. */
export async function getPendingRecords() {
  const db = await getDb()
  const all = await db.getAll(STORE)
  return all.sort((a, b) => a.createdAt - b.createdAt)
}

/**
 * Always reads IndexedDB fresh, never a cached in-memory value: a reload or
 * a fresh launch must show the true persisted count immediately, not a stale
 * zero left over from before the app closed.
 */
export async function getSummary() {
  const records = await getPendingRecords()
  return {
    total: records.length,
    stuck: records.filter((r) => r.stuck).length,
    // A dead session, not a bad record (task: stuck captures, 2026-09-08):
    // never counted toward stuck below, and resolves itself the moment the
    // rep is signed in again, so it gets its own count rather than either
    // hiding inside "stuck" (which would make it look like bad data) or
    // inside the plain pending count (which would make it look like it just
    // needs signal, when what it actually needs is a fresh login).
    authExpired: records.filter((r) => r.authExpired).length,
  }
}

// ---------------------------------------------------------------------------
// flushing
// ---------------------------------------------------------------------------

async function flushOne(record) {
  let badgeKey = record.badgeImageKey
  if (!badgeKey && record.badgeBlob) {
    const { key } = await base44.integrations.Core.UploadFile({ file: record.badgeBlob, kind: 'badge', background: true })
    badgeKey = key
    await updateRecord(record.clientUuid, { badgeImageKey: key })
  }

  // The back of the same card, task: two-sided business card capture
  // (2026-09-08). Uploaded the same plain, OCR-free way as the front: OCR
  // never runs on a queued offline capture at all (the rep already typed
  // the fields by hand), so there is nothing to merge here, just a second
  // key to attach.
  let badgeBackKey = record.badgeBackImageKey
  if (!badgeBackKey && record.badgeBackBlob) {
    const { key } = await base44.integrations.Core.UploadFile({ file: record.badgeBackBlob, kind: 'badge', background: true })
    badgeBackKey = key
    await updateRecord(record.clientUuid, { badgeBackImageKey: key })
  }

  let personKey = record.personImageKey
  if (!personKey && record.personBlob) {
    const { key } = await base44.integrations.Core.UploadFile({ file: record.personBlob, kind: 'person', background: true })
    personKey = key
    await updateRecord(record.clientUuid, { personImageKey: key })
  }

  await base44.functions.invoke('createMeeting', {
    data: {
      ...record.payload,
      ...(badgeKey ? { badge_image_key: badgeKey } : {}),
      ...(badgeBackKey ? { badge_back_image_key: badgeBackKey } : {}),
      ...(personKey ? { person_photo_key: personKey } : {}),
    },
    background: true,
  })

  await deleteRecord(record.clientUuid)
}

/**
 * One record's worth of the try/flushOne/categorize-the-failure logic,
 * shared by the automatic sweep below and a single explicit Retry (task:
 * stuck captures, 2026-09-08), so the two can never categorize the same
 * failure two different ways.
 *
 * Three outcomes for a failure, not two: status 0 (still offline, or the
 * request never reached the server) never counts toward MAX_SERVER_ATTEMPTS,
 * same as before. status 401 is new: previously folded into "the server
 * responded, so it counts", which meant a rep's session dying mid-show could
 * flag a perfectly good record as permanently stuck after 3 rejections that
 * were never about the data at all. A 401 now never counts either, and never
 * marks a record stuck, since a fresh login is all that record needs, not a
 * human fixing anything about it. Every other real response from the server
 * (a genuine 400, a stubborn 500) is the only thing that still counts, since
 * that is the only case where retrying the same stored payload will keep
 * failing the same way.
 */
async function attemptRecord(record) {
  try {
    await flushOne(record)
    return { ok: true }
  } catch (err) {
    const patch = {
      lastAttemptAt: Date.now(),
      lastError: err.message || 'Sync failed.',
    }
    if (err.status === 401) {
      patch.authExpired = true
      // Deliberately not touched: serverAttemptCount, stuck. See this
      // function's own comment above.
    } else if (typeof err.status === 'number' && err.status > 0) {
      // A real, non-401 response: whatever was wrong with the session
      // before is not what is wrong now, so any stale authExpired flag is
      // cleared alongside counting this genuine rejection.
      patch.authExpired = false
      const serverAttemptCount = (record.serverAttemptCount || 0) + 1
      patch.serverAttemptCount = serverAttemptCount
      patch.stuck = serverAttemptCount >= MAX_SERVER_ATTEMPTS
    }
    // status 0 (still offline, request never reached the server): patch
    // touches neither authExpired nor stuck/serverAttemptCount, same
    // reasoning as the stuck cap already used before this task existed. A
    // network hiccup proves nothing about whether the session or the data
    // is the problem, so neither flag should move.
    await updateRecord(record.clientUuid, patch)
    return { ok: false, error: patch.lastError }
  }
}

let flushInFlight = null

/**
 * Attempt every queued record that is not already flagged stuck, oldest
 * first, one at a time. Safe to call from an 'online' listener, a periodic
 * timer and a manual tap all at once: concurrent calls share the one run in
 * flight rather than racing each other.
 *
 * Stuck records are skipped here on purpose (task: stuck captures,
 * 2026-09-08): before this, a record that had already been rejected 3 times
 * kept being retried anyway, on the same 25 second timer as everything else,
 * forever, for a payload that had not changed and would fail the same way
 * every time. That is pure waste, and it is also why the red banner tap
 * used to look like it "did nothing": the retry it fired was silently
 * failing the same way it always had. A stuck record now only ever gets
 * retried through retryRecord below, a deliberate, one-record action from
 * the Stuck Captures screen.
 */
export function flushOutbox() {
  if (flushInFlight) return flushInFlight
  flushInFlight = (async () => {
    try {
      const records = await getPendingRecords()
      for (const record of records) {
        if (record.stuck) continue
        await attemptRecord(record)
      }
    } finally {
      flushInFlight = null
      notifyChanged()
    }
  })()
  return flushInFlight
}

/**
 * One explicit, rep initiated retry of a single stuck (or auth expired)
 * record, from the Stuck Captures screen. Bypasses flushOutbox's own
 * stuck-skip above and its shared in-flight guard, since this is a
 * deliberate action on one specific record, not the automatic sweep.
 * Returns { ok: true } or { ok: false, error }, so the screen can show a
 * real result instead of the record just silently sitting there again.
 */
export async function retryRecord(clientUuid) {
  const db = await getDb()
  const record = await db.get(STORE, clientUuid)
  if (!record) return { ok: false, error: 'This capture is gone; it may have already synced.' }
  const result = await attemptRecord(record)
  notifyChanged()
  return result
}
